import { NgForOf, NgSwitch, NgSwitchCase } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import {  Subscription } from 'rxjs';
import { BullhornFieldType, Entities } from '../../data-rules-model';
import { ICustomPositioningConfig } from '../../lib/custom-component-overlay.directive';
import { StaticOptionsProvider } from '../../lib/option-items-providers';
import { IEntityItem, PairedAutocompleteComponent } from '../../lib/paired-autocomplete/paired-autocomplete.component';
import { IActionFieldMeta, MetaDataService, MetaSource } from '../../meta-data.service';
import { transitionNewRowInOut } from '../../rule/kyloe-animations';
import { BaseActionComponent } from '../base-action.component';

@Component({
  selector: 'app-action-math',
  templateUrl: './math-operation.component.html',
  styleUrls: ['./math-operation.component.scss'],
  animations: [transitionNewRowInOut],
  imports: [
    MatButtonToggleGroup,
    MatButtonToggle,
    NgSwitchCase,
    MatSelect,
    MatTooltip,
    PairedAutocompleteComponent,
    MatOption,
    MatDivider,
    MatIcon,
    ReactiveFormsModule,
    NgForOf,
    NgSwitch
  ],
  encapsulation: ViewEncapsulation.None
})
export class MathOperatorComponent extends BaseActionComponent implements AfterViewInit, OnDestroy {
  sourceIdControl1: FormControl;
  sourceIdControl2: FormControl;
  fieldValueControl1: FormControl;
  fieldValueControl2: FormControl;
  toManyPlaceholderText1: string;
  toManyPlaceholderText2: string;
  errorMessage: string;
  errorMsgField = 'You are dividing by a field. If field is \'0\' this record will be skipped';
  errorMsgValue = 'You cannot divide by 0';
  sourceSecondaryDescriptor1: FormControl;
  sourceSecondaryDescriptor2: FormControl;
  @Input() primaryEntity: Entities;
  entitiesOptions1: IEntityItem[];
  entitiesOptions2: IEntityItem[];
  dataSourceType = MetaSource.ACTION;
  destinationControl1 = null;
  destinationControl2 = null;

  overlayPositioningConfig: ICustomPositioningConfig = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
    offsetX: -20
  };

  sourceSecondaryDescriptor1$: Subscription;
  sourceSecondaryDescriptor2$: Subscription;
  sourceIdControl1$: Subscription;
  sourceIdControl2$: Subscription;
  numberInput1$: Subscription;
  numberInput2$: Subscription;
  isVisible: boolean;
  messageResult: string;
  roundTypes = [];
  roundTypesLabels = RoundType;
  selectedRoundType: FormControl;
  mathOperators = [];
  operators = mathOperators;
  selectedObjectType1 = null;
  selectedObjectTypeForm1: FormControl;
  selectedObjectType2 = null;
  selectedObjectTypeForm2: FormControl;
  selectedOperator: FormControl;
  formFieldType1 = ObjectType;
  formFieldType2 = ObjectType;
  mathFields: MathFields;
  parameter1: Parameter;
  parameter2: Parameter;
  numberInput1: FormControl;
  numberInput2: FormControl;
  valuePrecisionForm: FormControl;
  sourceEntityForm1: FormControl;
  sourceEntityForm2: FormControl;
  formatGroup: FormGroup;
  parametersFormGroup1: FormGroup;
  parametersFormGroup2: FormGroup;
  parametersForm: FormArray;
  firstParameter: string;
  secondParameter: string;
  operationValue: string;
  initialMessage: string;
  disablePrecision = false;

  @ViewChild('sourceId1')
  sourceIdPickerComponent1: PairedAutocompleteComponent;
  @ViewChild('sourceId2')
  sourceIdPickerComponent2: PairedAutocompleteComponent;

   constructor(
     private formBuilder: FormBuilder,
     protected metaDataService: MetaDataService
   ) {
    super(metaDataService);
  }

  initialise(): void {
    this.createLocalControls();
    this.loadEntities();
    if (this.destinationTypeControl.value === BullhornFieldType.INTEGER) {
        this.valuePrecisionForm.setValue(0);
        this.disablePrecision = true;
    }
   this.loadComponentsData();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.formGroup.removeControl('value');
    this.formGroup.addControl('value', new FormControl(null));
  }

  ngAfterViewInit() {

  }

  loadComponentsData(): void {
    this.selectedOperator.setValue('ADD');
    this.isVisible = false;
    this.initialMessage = `${this.destinationControl.value} = `;
    this.operationValue = ` ${mathOperators.ADD} `;
    if (this.actionData.value)  {
      this.mathFields = this.actionData.value as MathFields;
      this.selectedOperator.setValue(this.mathFields.operator);
      this.selectedRoundType.setValue(this.mathFields.roundType);
      this.valuePrecisionForm.setValue(this.mathFields.precision);
      this.operationValue = (this.operators[this.selectedOperator.value] === undefined ?
                                      ` ${this.selectedOperator.value} ` : ` ${this.operators[this.selectedOperator.value]} `);
      this.selectedObjectTypeForm1.setValue(this.mathFields.parameters[0].type);
      this.selectedObjectTypeForm2.setValue(this.mathFields.parameters[1].type);
      this.parametersFormGroup1.addControl('type', this.selectedObjectTypeForm1);
      this.parametersFormGroup2.addControl('type', this.selectedObjectTypeForm2);
      if (this.mathFields.parameters[0].type === ObjectType.FIELD) {
        this.parametersFormGroup1.addControl('sourceEntity', this.sourceSecondaryDescriptor1);
        this.parametersFormGroup1.addControl('value', this.fieldValueControl1);
        this.formGroup.addControl('fieldValue1', this.sourceIdControl1);
        this.selectedObjectType1 = ObjectType.FIELD;
        if (this.sourceIdControl1$) {
          this.sourceIdControl1$.unsubscribe();
        }
        if (this.sourceSecondaryDescriptor1$) {
          this.sourceSecondaryDescriptor1$.unsubscribe();
        }
        this.sourceIdControl1$ = this.sourceIdControl1.valueChanges.subscribe(sourceId1Value =>
          this.sourceAutocomplete1(sourceId1Value));
        this.sourceSecondaryDescriptor1$ = this.sourceSecondaryDescriptor1.valueChanges.subscribe(value =>
          this.fieldToChangeDescriptorControlValueChangeHandler1(value));
        this.destinationControl1 = this.lowerFirstLetter(this.mathFields.parameters[0].sourceEntity);
        this.sourceSecondaryDescriptor1.setValue(this.destinationControl1);
        this.sourceIdControl1.setValue(this.formatEntityValue(this.mathFields.parameters[0].value));
        this.firstParameter = this.sourceIdControl1.value;
        this.messageResult = this.initialMessage + this.sourceIdControl1.value + this.operationValue;
      } else if (this.mathFields.parameters[0].type === ObjectType.VALUE) {
        this.parametersFormGroup1.addControl('value', this.numberInput1);
        this.selectedObjectType1 = ObjectType.VALUE;
        this.numberInput1.setValue(this.mathFields.parameters[0].value);
        this.firstParameter = this.numberInput1.value;
        this.messageResult = this.initialMessage + this.numberInput1.value + this.operationValue;
        if (this.numberInput1$) {
          this.numberInput1$.unsubscribe();
        }
        this.numberInput1$ = this.numberInput1.valueChanges.subscribe(param1Value =>
          this.valueFormControlChange1(param1Value));
      }

      if (this.mathFields.parameters[1].type === ObjectType.FIELD) {
        this.parametersFormGroup2.addControl('sourceEntity', this.sourceSecondaryDescriptor2);
        this.parametersFormGroup2.addControl('value', this.fieldValueControl2);
        this.formGroup.addControl('fieldValue2', this.sourceIdControl2);
        this.selectedObjectType2 = ObjectType.FIELD;
        this.destinationControl2 = this.lowerFirstLetter(this.mathFields.parameters[1].sourceEntity);
        if (this.sourceIdControl2$)
          this.sourceIdControl2$.unsubscribe();

        if (this.sourceSecondaryDescriptor2$)
          this.sourceSecondaryDescriptor2$.unsubscribe();

        this.sourceIdControl2$ = this.sourceIdControl2.valueChanges.subscribe(sourceId2value =>
          this.sourceAutocomplete2(sourceId2value));
        this.sourceSecondaryDescriptor2$ = this.sourceSecondaryDescriptor2.valueChanges.subscribe(value =>
          this.fieldToChangeDescriptorControlValueChangeHandler2(value));
        this.sourceSecondaryDescriptor2.setValue(this.destinationControl2);
        this.sourceIdControl2.setValue(this.formatEntityValue(this.mathFields.parameters[1].value));
        this.secondParameter = this.sourceIdControl2.value;
      } else if (this.mathFields.parameters[1].type === ObjectType.VALUE) {
        this.parametersFormGroup2.addControl('value', this.numberInput2);
        this.selectedObjectType2 = ObjectType.VALUE;
        this.numberInput2.setValue(this.mathFields.parameters[1].value);
        this.messageResult = this.messageResult + this.numberInput2.value;
        this.secondParameter = this.numberInput2.value;
        if (this.numberInput2$)
          this.numberInput2$.unsubscribe();

        this.numberInput2$ =  this.numberInput2.valueChanges.subscribe(param2Value =>
          this.valueFormControlChange2(param2Value));
      }
    } else {
      this.changeEventFormTypeValue1(ObjectType.FIELD);
      this.changeEventFormTypeValue2(ObjectType.FIELD);
    }

    this.mathOperators = Object.keys(mathOperators);
    this.roundTypes = Object.keys(RoundType);
    this.valuePrecisionForm.valueChanges.subscribe(value => this.valuePrecisionFormControlChange(value));
    this.formatGroup.valueChanges.subscribe(_ => this.valueControlValueChange());
    this.valueControlValueChange();

    setTimeout(() => {
      this.getMeta(this.destinationDescriptorControl.value)
        .subscribe((filteredMeta: IActionFieldMeta[]) => {
          const selField = filteredMeta.find(fm => fm.fieldName === this.destinationControl.value);
          this.sourceControl.setValidators(null);
          this.destinationControl.setValidators(Validators.required);
          //this.destinationControlValueChange(selField);
        });
    }, 0);

  }

  private loadPickersData(pickerComponent : PairedAutocompleteComponent, pickerControl : FormControl,
                         descriptorEntity: string, pickerPlaceHolderText: Object){
      pickerComponent.clearOption();
      this.getMeta(descriptorEntity)
        .subscribe((filteredMeta: IActionFieldMeta[]) => {
          pickerPlaceHolderText= 'Loading...';
          const options = filteredMeta.filter(fm => this.isAllowed(fm.metaType)).map(fm => ({name: fm.fieldName, label: fm.label}));
          const optionsProvider = new StaticOptionsProvider(options);
          pickerComponent.setOptionsProvider(optionsProvider, pickerControl.value);
          pickerPlaceHolderText = undefined;
        });
  }

  protected fieldToChangeDescriptorControlValueChangeHandler1(descriptor: string): void {
    this.destinationControl1 = descriptor;
    setTimeout(() => {
      this.loadPickersData(this.sourceIdPickerComponent1,this.sourceIdControl1, descriptor,this.toManyPlaceholderText1);
    }, 0);
  }

  protected fieldToChangeDescriptorControlValueChangeHandler2(descriptor: string): void {
    this.destinationControl2 = descriptor;
    setTimeout(() => {
      this.loadPickersData(this.sourceIdPickerComponent2,this.sourceIdControl2, descriptor,this.toManyPlaceholderText2);
    }, 0);
  }

  protected  loadEntities(): void {
    this.metaDataService.reduceMetaToEntitiesOptions(this.primaryEntity, MetaSource.ACTION)
      .subscribe(entities => {
        this.entitiesOptions1 = entities;
        this.entitiesOptions2 = entities;
      });
  }

  protected destinationControlValueChange(selectedField: IActionFieldMeta): void {

    if (!selectedField) {
      this.valueControl.setValue(null);
      this.destinationTypeControl.setValue(null);
      return;
    }
    if (this.destinationTypeControl.value !== selectedField.metaType)
      this.valueControl.setValue(MathOperatorComponent.getDefaultValue(selectedField.metaType));

    this.destinationTypeControl.setValue(selectedField.metaType);
    this.sourceControl.setValue(this.actionData.source);
    this.selectedRoundType.setValidators(Validators.required);
  }

  private static getDefaultValue(type: string): any {
    switch (type) {
      case 'STRING':
        return '';
      default:
        return null;
    }
  }

  private lowerFirstLetter(value: string) {
    if (value)
      return value.charAt(0).toLowerCase() + value.slice(1);
  }

  private formatEntityValue(value: string) {
    if (value) {
      if (value.indexOf('.') !== -1) {
        value = value.split('.')[1];
      }
      return value;
    }
  }

  isAllowed(value: string) {
    switch (value) {
      case 'INTEGER':
        return true;
      case 'DOUBLE':
        return true;
      case 'BIG_DECIMAL':
        return true;
      default:
        return false;
    }

  }

  updateOperationMessage(): void {
    this.messageResult = this.initialMessage
      + (this.firstParameter === undefined || this.firstParameter === null ? ' ' : this.firstParameter)
      + (this.operationValue === undefined || this.operationValue === null ? ' ' : this.operationValue)
      + (this.secondParameter === undefined || this.secondParameter === null ? ' ' : this.secondParameter);
  }

  changeEventFormTypeValue1(value: ObjectType): void {
    this.parametersFormGroup1.removeControl('sourceEntity');
    this.parametersFormGroup1.removeControl('type');
    this.parametersFormGroup1.removeControl('value');
    this.formGroup.removeControl('fieldValue1');
    if (value === ObjectType.FIELD) {
      this.loadEntities();
      this.formGroup.addControl('fieldValue1', this.sourceIdControl1);
      this.selectedObjectType1 = ObjectType.FIELD;
      this.selectedObjectTypeForm1.setValue(this.selectedObjectType1);
      if (this.sourceIdControl1$) {
        this.sourceIdControl1$.unsubscribe();
      }
      if (this.sourceSecondaryDescriptor1$) {
        this.sourceSecondaryDescriptor1$.unsubscribe();
      }

      if (this.sourceSecondaryDescriptor1.value) {
        this.parameter1.sourceEntity = this.sourceSecondaryDescriptor1.value;
      }
      this.parametersFormGroup1.addControl('sourceEntity', this.sourceSecondaryDescriptor1);
      this.parametersFormGroup1.addControl('type', this.selectedObjectTypeForm1);
      this.parametersFormGroup1.addControl('value', this.fieldValueControl1);
      this.sourceIdControl1$ = this.sourceIdControl1.valueChanges.subscribe(sourceId1Value =>
        this.sourceAutocomplete1(sourceId1Value));
      this.sourceSecondaryDescriptor1$ = this.sourceSecondaryDescriptor1.valueChanges.subscribe(sourceId1DesValue =>
        this.fieldToChangeDescriptorControlValueChangeHandler1(sourceId1DesValue));
      setTimeout(() => {
         this.loadPickersData(this.sourceIdPickerComponent1,this.sourceIdControl1, this.destinationControl1,this.toManyPlaceholderText1);
      }, 0);
    } else if (value === ObjectType.VALUE) {
      this.selectedObjectType1 = ObjectType.VALUE;
      this.selectedObjectTypeForm1.setValue(this.selectedObjectType1);
      if (this.numberInput1$) {
        this.numberInput1$.unsubscribe();
      }
      this.parametersFormGroup1.addControl('type', this.selectedObjectTypeForm1);
      this.parametersFormGroup1.addControl('value', this.numberInput1);
      this.numberInput1$ = this.numberInput1.valueChanges.subscribe(input1Value =>
        this.valueFormControlChange1(input1Value));
    }
  }

  sourceAutocomplete1(value: string) {
    this.firstParameter = value ;
    this.updateOperationMessage();
    if (this.destinationControl1 !== null && this.destinationControl1 !== undefined) {
      this.fieldValueControl1.setValue(this.destinationControl1 + '.' + value);
    } else {
      this.fieldValueControl1.setValue(value);
    }
  }

  sourceAutocomplete2(value: string) {
    this.secondParameter = value ;
    if (this.operators[this.selectedOperator.value] !== mathOperators.DIVIDE) {
      this.isVisible = false;
    }
    if (this.destinationControl2 !== null && this.destinationControl2 !== undefined) {
      this.fieldValueControl2.setValue(this.destinationControl2 + '.' + value);
    } else {
      this.fieldValueControl2.setValue(value);
    }
    this.updateOperationMessage();
  }

  changeEventFormTypeValue2(value: ObjectType): void {
    this.parametersFormGroup2.removeControl('sourceEntity');
    this.parametersFormGroup2.removeControl('type');
    this.parametersFormGroup2.removeControl('value');
    this.formGroup.removeControl('fieldValue2');
    if (value === ObjectType.FIELD) {
      this.loadEntities();
      this.formGroup.addControl('fieldValue2', this.sourceIdControl2);
      this.errorMessage = this.errorMsgField;
      this.selectedObjectType2 = ObjectType.FIELD;
      this.selectedObjectTypeForm2.setValue(this.selectedObjectType2);
      if (this.sourceIdControl2$) {
        this.sourceIdControl2$.unsubscribe();
      }
      if (this.sourceSecondaryDescriptor2$) {
        this.sourceSecondaryDescriptor2$.unsubscribe();
      }
      if (this.sourceSecondaryDescriptor2.value) {
        this.parameter2.sourceEntity = this.sourceSecondaryDescriptor2.value;
      }
      this.parametersFormGroup2.addControl('sourceEntity', this.sourceSecondaryDescriptor2);
      this.parametersFormGroup2.addControl('type', this.selectedObjectTypeForm2);
      this.parametersFormGroup2.addControl('value', this.fieldValueControl2);

      this.sourceIdControl2$ = this.sourceIdControl2.valueChanges.subscribe(sourceId2Value =>
        this.sourceAutocomplete2(sourceId2Value));
      this.sourceSecondaryDescriptor2$ = this.sourceSecondaryDescriptor2.valueChanges.subscribe(sourceIdDes2Value =>
        this.fieldToChangeDescriptorControlValueChangeHandler2(sourceIdDes2Value));
      setTimeout(() => {
        this.loadPickersData(this.sourceIdPickerComponent2,this.sourceIdControl2, this.destinationControl2,this.toManyPlaceholderText2);
      }, 0);
    } else if (value === ObjectType.VALUE) {
      this.errorMessage = this.errorMsgValue;
      this.selectedObjectType2 = ObjectType.VALUE;
      this.selectedObjectTypeForm2.setValue(this.selectedObjectType2);
      if (this.numberInput2$)
        this.numberInput2$.unsubscribe();

      this.numberInput2$ = this.numberInput2.valueChanges.subscribe(input2Value =>
        this.valueFormControlChange2(input2Value));
    }
    this.parametersFormGroup2.addControl('type', this.selectedObjectTypeForm2);
    this.parametersFormGroup2.addControl('value', this.numberInput2);
  }

  valueFormControlChange1(value: string) {
    this.firstParameter = value;
    this.updateOperationMessage();
  }

  valueFormControlChange2(value: string) {
    this.secondParameter = value;
    if (this.operators[this.selectedOperator.value] !== mathOperators.DIVIDE || Number(value) > 0) {
      this.isVisible = false;
    }
    this.updateOperationMessage();
  }

  valuePrecisionFormControlChange(value: number) {
    this.mathFields.precision = value;
  }

  changeRoundType(value: RoundType) {
    this.mathFields.roundType = value;
  }

  changeEventOperator(value: mathOperators) {
    this.operationValue = ` ${this.operators[this.selectedOperator.value]} `;
    this.updateOperationMessage();
  }

  createLocalControls(): void {
    this.formatGroup = this.formBuilder.group([]);
    this.parametersFormGroup1 = this.formBuilder.group([]);
    this.parametersFormGroup2 = this.formBuilder.group([]);
    this.parametersForm = this.formBuilder.array([]);
    this.formGroup.removeControl('value');
    this.formGroup.removeControl('fieldValue1');
    this.formGroup.removeControl('fieldValue2');
    this.formGroup.removeControl('operator');
    this.formGroup.removeControl('roundType');
    this.formGroup.removeControl('precision');
    this.formGroup.removeControl('parameters');
    this.parametersForm.removeAt(0);
    this.parametersForm.removeAt(1);
    this.selectedOperator = new FormControl();
    this.selectedRoundType = new FormControl();
    this.sourceIdControl1 = new FormControl();
    this.sourceIdControl2 = new FormControl();
    this.fieldValueControl1 = new FormControl();
    this.fieldValueControl2 = new FormControl();
    this.selectedObjectTypeForm1 = new FormControl();
    this.selectedObjectTypeForm2 = new FormControl();
    this.mathFields = new MathFields();
    this.mathFields.operator = mathOperators.ADD;
    this.parameter1 = new Parameter();
    this.parameter2 = new Parameter();
    this.numberInput1 = new FormControl();
    this.numberInput2 = new FormControl();
    this.valuePrecisionForm = new FormControl();
    this.sourceSecondaryDescriptor1 = new FormControl();
    this.sourceSecondaryDescriptor2 = new FormControl();
    this.mathFields.precision = 0;

    this.sourceEntityForm1 = new FormControl();
    this.sourceEntityForm2 = new FormControl();

    this.parametersForm.insert(0, this.parametersFormGroup1);
    this.parametersForm.insert(1, this.parametersFormGroup2);
    this.formatGroup.addControl('operator', this.selectedOperator);
    this.formatGroup.addControl('roundType', this.selectedRoundType);
    this.formatGroup.addControl('precision', this.valuePrecisionForm);
    this.formatGroup.addControl('parameters', this.parametersForm);
    this.formGroup.addControl('value', this.formatGroup);
  }

  private valueControlValueChange(): void {
    if (this.selectedRoundType.value === null || this.selectedRoundType.value.trimStart().length === 0) {
      this.formatGroup.setErrors({invalid: true});
    }
    if (this.selectedOperator.value === null) {
      this.formatGroup.setErrors({invalid: true});
    }
    if (this.selectedObjectType1 === null) {
      this.formatGroup.setErrors({invalid: true});
    }
    if (this.selectedObjectType2 === null) {
      this.formatGroup.setErrors({invalid: true});
    }
    if (this.selectedObjectType1 === ObjectType.FIELD
      && (this.sourceIdControl1.value === null
        || this.sourceIdControl1.value.length === 0)) {
      this.formatGroup.setErrors({invalid: true});
    } else if (this.selectedObjectType1 === ObjectType.VALUE
      && (this.numberInput1.value === null
        || this.numberInput1.value.length === 0)) {
      this.formatGroup.setErrors({invalid: true});
    }
    if (this.selectedObjectType2 === ObjectType.FIELD
      && (this.sourceIdControl2.value === null
        || this.sourceIdControl2.value.length === 0)) {
      this.formatGroup.setErrors({invalid: true});
    } else if (this.selectedObjectType2 === ObjectType.VALUE
      && (this.numberInput2.value === null
        || this.numberInput2.value.length === 0)) {
      this.formatGroup.setErrors({invalid: true});
    }

    if ((this.operators[this.selectedOperator.value] === mathOperators.DIVIDE
      || this.operators[this.selectedOperator.value] === 'DIVIDE')
      && this.selectedObjectType2 === ObjectType.FIELD) {
      this.isVisible = true;
      this.errorMessage = this.errorMsgField;
    } else if ((this.operators[this.selectedOperator.value] === mathOperators.DIVIDE
      || this.operators[this.selectedOperator.value] === 'DIVIDE')
      && this.selectedObjectType2 === ObjectType.VALUE
      && (this.numberInput2.value === null || this.numberInput2.value === 0)) {
      this.formatGroup.setErrors({invalid: true});
      this.errorMessage = this.errorMsgValue;
      this.isVisible = true;
    } else {
      this.isVisible = false;
    }

    if (this.valuePrecisionForm.value === null) {
      this.formatGroup.setErrors({invalid: true});
    }

  }

}

export enum mathOperators {
  MULTIPLY = '×',
  DIVIDE = '÷',
  ADD = '+',
  SUBTRACT = '−'
}

export enum RoundType {
  ROUND_DOWN = 'Round down to',
  ROUND_UP = 'Round up to',
  ROUND_NEAREST = 'Round to nearest'
}

export enum ObjectType {
  VALUE = 'VALUE',
  FIELD = 'FIELD'
}

export class Parameter {
  sourceEntity: string;
  type: ObjectType;
  value: string;
}

export class MathFields {
  operator: mathOperators;
  roundType: RoundType;
  precision: number;
  parameters: Parameter[] = [];
}
